import { auth, db } from "../../firebase.js";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  deleteDoc,
} from "firebase/firestore";
import { useCallback, useContext, useEffect, useState } from "react";
import "./Chatbar.css";
import { Menu } from "react-pro-sidebar";
import { IsMobileContext, UserInfoContext } from "../../contexts";
import { useDropzone } from "react-dropzone";
import {
  dateReactToFirestore,
  sendAdminMessageNotiication,
  uploadFileToFirestore,
} from "../../helpers";
import documentPreviewIcon from "../../assets/document_preview.svg";
import Modal from "react-modal";
import { BsXCircle } from "react-icons/bs";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { useRef } from "react";
import InputArea from "../reusables/MessagesContainer/InputArea.js";
import TypingIndicator from "../reusables/MessagesContainer/TypingIndicator.js";
import Messages from "../reusables/MessagesContainer/Messages.js";

const ChatbarView = (props) => {
  const userInfo = useContext(UserInfoContext);
  const [channelMessages, setChannelMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [messageFiles, setMessageFiles] = useState([]);
  const [activeFileDrag, seActiveFileDrag] = useState(false);

  const [channelDoc, setChannelDoc] = useState(null);

  const [userIsTyping, setUserIsTyping] = useState(false);
  const [otherIsTyping, setOtherIsTyping] = useState(false);

  const [chatBarHeight, setChatBarHeight] = useState(152);

  const [hoverOnMessage, setHoverOnMessage] = useState(null);
  const [messageDropdownOpen, setMessageDropdownOpen] = useState(false);
  const [isEditingMessage, setIsEditingMessage] = useState(false);
  const [idToEdit, setIdToEdit] = useState(null);

  const [replyingTo, setReplyingTo] = useState(null);
  const [replyToPadding, setReplyToPadding] = useState(0);

  // Used to autofocus the textarea programatically
  const textareaRef = useRef(null);

  const [loading, setLoading] = useState(true);

  const [currentTarget, setCurrentTarget] = useState(null);
  const [flipMessageOptions, setFlipMessageOptions] = useState(false);

  const chatRef = useRef(null);

  const [shouldEmailAdmin, setShouldEmailAdmin] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (!currentTarget || !chatRef.current || !messageDropdownOpen) return;

      const { top: clickedTop } = currentTarget.getBoundingClientRect();
      const { top: chatTop, bottom: chatBottom } =
        chatRef.current.getBoundingClientRect();
      const relativeTop = clickedTop - chatTop;

      setFlipMessageOptions(
        relativeTop >= 200 && chatBottom - relativeTop - 350 < 0,
      );
    };

    const divElement = chatRef.current;
    if (divElement) {
      divElement.addEventListener("scroll", handleScroll);
      return () => {
        divElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [currentTarget, messageDropdownOpen]);

  /**
   * This function will be called when the user drops files into the dropzone or selects files via the file input
   * @param acceptedFiles - the files that were dropped or selected
   */
  function queueFiles(acceptedFiles) {
    setMessageFiles((prevState) => [
      ...prevState,
      ...acceptedFiles.map((file) =>
        Object.assign(file, { preview: URL.createObjectURL(file) }),
      ),
    ]);
  }

  const uploadFileViaButton = useCallback((acceptedFiles) => {
    const uploadedFiles = acceptedFiles.target.files;
    if (uploadedFiles.length > 0) queueFiles(Array.from(uploadedFiles));
  }, []);

  /**
   * Handlers for drag and drop into the chatbar.
   */

  const onDrop = useCallback((acceptedFiles) => {
    onDragLeave();
    queueFiles(acceptedFiles);
  }, []);

  const onDragEnter = useCallback(() => {
    seActiveFileDrag(true);
  }, []);

  const onDragLeave = useCallback(() => {
    seActiveFileDrag(false);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDragEnter,
    onDragLeave,
    onDrop,
  });
  /**
   * This will be called when a different channel is selected, and will update the chatbar messages in real time.
   */
  useEffect(() => {
    setLoading(true);
    resetMessage();
    setMessage("");
    if (!props?.openedChannel?.id) {
      return;
    }
    const channelRef = doc(db, "channels", props.openedChannel.id);

    if (channelRef && auth?.currentUser?.uid) {
      updateDoc(channelRef, {
        typingUsers: arrayRemove(auth?.currentUser?.uid),
      });
    }
    let unsubscribeMessages;
    let unsubscribeChannel;
    if (props.userChannels?.length && props.openedChannel) {
      unsubscribeMessages = onSnapshot(
        collection(doc(db, "channels", props.openedChannel.id), "messages"),
        (snapshot) => {
          let messageData = [];
          snapshot.forEach((_message) => {
            messageData.push({ id: _message.id, ..._message.data() });
          });
          messageData.sort((a, b) => b.created - a.created);
          setChannelMessages(messageData);
          setLoading(false);
        },
      );

      unsubscribeChannel = onSnapshot(
        doc(db, "channels", props.openedChannel.id),
        (channelDoc) => {
          setChannelDoc(channelDoc.data());
        },
      );
    }

    // Cleanup function
    return () => {
      if (unsubscribeMessages) unsubscribeMessages();
      if (unsubscribeChannel) unsubscribeChannel();
    };
  }, [props.openedChannel, props.userChannels]);

  useEffect(() => {
    if (channelMessages) {
      updateLastReadTime();
      resetUnreadMessages(auth.currentUser.uid);
    }
  }, [channelMessages]);

  async function updateLastReadTime() {
    if (props?.openedChannel?.id) {
      const channelRef = doc(db, "channels", props.openedChannel.id);
      if (!channelDoc || !channelRef) return;

      // If the map lastRead does not exist, create it
      if (!channelDoc.lastRead) {
        const updatePayload = {
          lastRead: {
            [auth.currentUser.uid]: Date.now(),
          },
        };
        updateDoc(channelRef, updatePayload);
      } else {
        const updatedLastRead = { ...channelDoc.lastRead };
        updatedLastRead[auth.currentUser.uid] = Date.now();
        const updatePayload = { lastRead: updatedLastRead };
        updateDoc(channelRef, updatePayload);
      }
    }
  }

  // When user textarea changes, update the typing indicator
  useEffect(() => {
    if (!props?.openedChannel?.id) return;

    const channelRef = doc(db, "channels", props.openedChannel.id);
    if (!channelDoc || !channelRef) return;

    const isTyping = channelDoc?.typingUsers?.includes(auth.currentUser.uid);

    if (auth.currentUser.uid) {
      if (message && !isTyping) {
        updateDoc(channelRef, {
          typingUsers: arrayUnion(auth?.currentUser?.uid),
        });
      } else if (!message && isTyping) {
        updateDoc(channelRef, {
          typingUsers: arrayRemove(auth?.currentUser?.uid),
        });
      }
    }

    const textarea = document.getElementById("msgcontent");
    if (textarea) {
      textarea.style.height = "auto"; // Temporarily reset height to calculate scrollHeight accurately
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = parseInt(getComputedStyle(textarea).maxHeight);

      let newHeight = scrollHeight;
      if (newHeight > maxHeight) {
        newHeight = maxHeight; // Ensure newHeight does not exceed maxHeight
      }

      textarea.style.height = newHeight + "px"; // Set the new height based on content
      setChatBarHeight(newHeight + 152 - 20); // Adjust chat bar height accordingly
    }

    if (message === "") {
      setIsEditingMessage(false);
      setIdToEdit(null);
    }
  }, [message]);

  // On page load remove typing indicator for user
  useEffect(() => {
    if (props?.openedChannel?.id && auth?.currentUser?.uid) {
      updateDoc(doc(db, "channels", props.openedChannel.id), {
        typingUsers: arrayRemove(auth?.currentUser?.uid),
      });
    }

    // When the user leaves chat, remove the typing indicator for them
    return () => {
      if (props?.openedChannel?.id && auth?.currentUser?.uid) {
        updateDoc(doc(db, "channels", props.openedChannel.id), {
          typingUsers: arrayRemove(auth?.currentUser?.uid),
        });
      }
    };
  }, []);

  useEffect(() => {
    if (props?.openedChannel?.id && channelDoc) {
      setUserIsTyping(channelDoc?.typingUsers?.includes(auth.currentUser.uid));
      setOtherIsTyping(
        channelDoc?.typingUsers?.length === 2 ||
          (channelDoc?.typingUsers?.length === 1 &&
            !channelDoc?.typingUsers?.includes(auth.currentUser.uid)),
      );
    }
  }, [channelDoc]);

  const incrementUnreadMessages = async () => {
    if (props?.openedChannel?.id && props?.openedChannel?.parties) {
      const otherUid = props.openedChannel.parties.filter(
        (party) => party !== auth.currentUser.uid,
      )[0];

      const channelRef = doc(db, "channels", props.openedChannel.id);
      if (!channelDoc || !channelRef) return;

      // If the map lastRead does not exist, create it
      if (!channelDoc.newMessagesCount) {
        const updatePayload = {
          newMessagesCount: {
            [otherUid]: 1,
          },
        };
        updateDoc(channelRef, updatePayload);
      } else {
        const updatedNewMessagesCount = { ...channelDoc.newMessagesCount };
        updatedNewMessagesCount[otherUid] =
          (isNaN(updatedNewMessagesCount[otherUid])
            ? 0
            : updatedNewMessagesCount[otherUid]) + 1;
        const updatePayload = { newMessagesCount: updatedNewMessagesCount };
        updateDoc(channelRef, updatePayload);
      }
    }
  };

  const resetUnreadMessages = async (uid) => {
    if (props?.openedChannel?.id) {
      const channelRef = doc(db, "channels", props.openedChannel.id);
      if (!channelDoc || !channelRef) return;
      const updatedNewMessagesCount = {
        ...channelDoc.newMessagesCount,
        [uid]: 0,
      };
      await updateDoc(channelRef, {
        newMessagesCount: updatedNewMessagesCount,
      });
    }
  };

  const updateLatestMessageTime = async () => {
    if (props?.openedChannel?.id && props?.openedChannel?.parties) {
      const channelRef = doc(db, "channels", props.openedChannel.id);
      if (!channelDoc || !channelRef) return;

      const updatePayload = {
        latestMessage: dateReactToFirestore(new Date()),
      };
      updateDoc(channelRef, updatePayload);
    }
  };

  /**
   * This function will be called when the user clicks the send button or presses enter.
   * It will upload the files to firestore and create a new message document.
   */
  const createNewMessage = async () => {
    setChatBarHeight(152);
    const tempMessageFiles = messageFiles;
    const tempMessage = message;

    setMessageFiles([]);
    setMessage("");
    const messagesRef = collection(
      doc(db, "channels", props.openedChannel.id),
      "messages",
    );

    const newDocRef = doc(messagesRef);

    const files = [];
    for (const file of tempMessageFiles) {
      files.push({
        url: await uploadFileToFirestore(
          "channels/" + props.openedChannel.id + "/messages/" + newDocRef.id,
          file,
        ),
        size: file.size,
      });
    }

    const now = Date.now();

    let newMessage = {
      body: {
        message: tempMessage,
        files: files,
      },
      created: now,
      uid: userInfo.uid,
    };

    if (replyingTo && replyingTo.id) {
      newMessage.replyingTo = replyingTo.id;
    }

    await setDoc(newDocRef, newMessage);
    await updateLatestMessageTime();

    if (
      props?.chatPartnerStatus !== "online" &&
      shouldEmailAdmin &&
      userInfo.role !== "admin"
    ) {
      setShouldEmailAdmin(false);
      sendAdminMessageNotiication(userInfo.firstName);
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState(null);

  const handleOpenModal = (imgSrc) => {
    setImgSrc(imgSrc);
    setIsModalOpen(true);
  };

  /**
   * This is the modal that will be displayed when the user clicks on an image in the chatbar.
   * @type {JSX.Element}
   */
  const modalContent = (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={() => setIsModalOpen(false)}
      contentLabel="Image Modal"
      appElement={document.getElementById("root")}
      style={{
        overlay: { zIndex: 10 },
        content: {
          width: "50%",
          height: "75%",
          margin: "auto",
        },
      }}
      className={"custom-modal modal-shape"}
    >
      <button onClick={() => setIsModalOpen(false)} className="icon-button">
        <BsXCircle alt="X button" size="30px" color="#1F1F24" />
      </button>
      <div className="modal-image-container">
        <img src={imgSrc} alt={"image"} />
      </div>
    </Modal>
  );

  /**
   * This function will be called when the user clicks on a file in the chatbar, and will open the file in a new tab.
   * @param file - the file object that was clicked on
   * @returns {(function(): void)|*}
   */
  const handleDownload = (file) => () => {
    const fileExtension = file.url.split(".").pop().toLowerCase().split("?")[0];
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp"];
    if (imageExtensions.includes(fileExtension)) {
      handleOpenModal(file.url);
    } else {
      // Regular download
      const anchor = document.createElement("a");
      anchor.href = file.url;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.download = file.name;

      anchor.click();
    }
  };

  const resetMessage = () => {
    setReplyToPadding(0);
    setIdToEdit(null);
    setReplyingTo(null);
    setMessageDropdownOpen(null);
    setHoverOnMessage(null);
    setIsEditingMessage(false);
  };

  const handleMessageEditClick = (message) => {
    resetMessage();
    setMessage("");
    setIsEditingMessage(true);
    setIdToEdit(message.id);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
    if (message.body.message.length < 1000) {
      if (Array.isArray(message.body.message)) {
        setMessage(message.body.message.join("\n"));
      } else {
        setMessage(message.body.message);
      }
    }
  };

  const handleSaveEditedMessage = () => {
    if (idToEdit) {
      if (props.openedChannel.id && idToEdit) {
        updateDoc(
          doc(
            collection(db, "channels", props.openedChannel.id, "messages"),
            idToEdit,
          ),
          { "body.message": message },
        );
      }
    }
  };

  const handleMessageDeleteClick = (id) => {
    resetMessage();
    setMessage("");

    if (props.openedChannel.id && id) {
      deleteDoc(
        doc(collection(db, "channels", props.openedChannel.id, "messages"), id),
      );
    }
  };

  const handleMessageReplyClick = (message) => {
    resetMessage();
    setMessage("");
    if (textareaRef.current) {
      textareaRef.current.focus();
    }

    // Find the message in channelMessages with the same id as message.id
    const foundMessage = channelMessages.find((m) => m.id === message.id);
    if (foundMessage) {
      // Creating a soft copy of foundMessage
      let messageCopy = { ...foundMessage, body: { ...foundMessage.body } };

      let replyToPadding = 76;
      if (messageCopy.body.files && messageCopy.body.files.length > 0) {
        replyToPadding += 62;
      }
      if (
        messageCopy.body.message === "" &&
        messageCopy.body.files.length === 1
      ) {
        replyToPadding -= 18;
      }

      if (messageCopy.body.files.length > 1) {
        messageCopy.body.message = `+${
          messageCopy.body.files.length - 1
        } more files ${messageCopy.body.message}`;
      }

      setReplyingTo(messageCopy);
      setReplyToPadding(replyToPadding);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDropdownClick = event.target.closest(
        ".message-options, .hover-box",
      );

      if (!isDropdownClick && messageDropdownOpen !== null) {
        setMessageDropdownOpen(null);
        setHoverOnMessage(null);
        return;
      }
    };

    if (messageDropdownOpen !== null) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => document.removeEventListener("click", handleClickOutside);
  }, [messageDropdownOpen]);

  return (
    <div>
      {isModalOpen && modalContent}
      <div
        style={{
          height: "100%",
          width: "100%",
          background: "white",
          border: "none",
          marginTop: "-85px",
        }}
      >
        <div
          style={{
            position: "absolute",
            bottom: `${chatBarHeight + replyToPadding + 48}px`,
            width: "100%",
            left: "0",
            transition: "0.3s",
          }}
        >
          <hr></hr>
        </div>

        <Menu
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "100%",
            justifyContent: "flex-end",
            overflow: "visible",
          }}
        >
          <div
            style={{
              direction: "ltr",
            }}
          >
            <Link to={"/"}>
              <div
                style={{
                  width: "158px",
                  height: "24px",
                  textAlign: "center",
                  marginTop: "37px",
                  marginLeft: "-16px",
                }}
                className="fs-14 fw-700 fc-black"
                onClick={() => {
                  props.handleChannelClick("null");
                }}
              >
                <FiArrowLeft
                  color="#000"
                  size="24px"
                  style={{ marginRight: "10px", marginBottom: "2px" }}
                />
                Back to Dashboard
              </div>
            </Link>
            <div style={{ marginLeft: "-10px", marginTop: "23px" }}>
              <hr></hr>
            </div>

            <div
              className="fw-700 fs-24 lh-324"
              style={{ marginTop: "50px", marginLeft: "-16px" }}
            >
              Message
            </div>
            <div {...getRootProps()} className="d-flex flex-column">
              {activeFileDrag ? (
                <div className="file-drop-chat-overlay">
                  <div className="file-drop-full-page-overlay__text">
                    Drop anywhere to upload
                  </div>
                </div>
              ) : null}

              <Messages
                chatRef={chatRef}
                loading={loading}
                channelMessages={channelMessages}
                userInfo={userInfo}
                props={props}
                // Below are optional
                // sending messages
                chatBarHeight={chatBarHeight}
                replyToPadding={replyToPadding}
                // images/downloading supported
                handleDownload={handleDownload}
                // Regular chat, so can interact with messages
                setCurrentTarget={setCurrentTarget}
                handleMessageEditClick={handleMessageEditClick}
                handleMessageDeleteClick={handleMessageDeleteClick}
                handleMessageReplyClick={handleMessageReplyClick}
                setFlipMessageOptions={setFlipMessageOptions}
                flipMessageOptions={flipMessageOptions}
                messageDropdownOpen={messageDropdownOpen}
                setMessageDropdownOpen={setMessageDropdownOpen}
                hoverOnMessage={hoverOnMessage}
                setHoverOnMessage={setHoverOnMessage}
              />
              <div
                style={{
                  position: "absolute",
                  bottom: "28px",
                  pointerEvents: "none",
                }}
              >
                <TypingIndicator
                  userInfo={userInfo}
                  props={props}
                  userIsTyping={userIsTyping}
                  otherIsTyping={otherIsTyping}
                  chatBarHeight={chatBarHeight}
                  replyToPadding={replyToPadding}
                />
                <InputArea
                  chatBarHeight={chatBarHeight}
                  replyToPadding={replyToPadding}
                  userInfo={userInfo}
                  replyingTo={replyingTo}
                  getInputProps={getInputProps}
                  textareaRef={textareaRef}
                  message={message}
                  setMessage={setMessage}
                  isEditingMessage={isEditingMessage}
                  setIsEditingMessage={setIsEditingMessage}
                  setIdToEdit={setIdToEdit}
                  handleSaveEditedMessage={handleSaveEditedMessage}
                  createNewMessage={createNewMessage}
                  resetMessage={resetMessage}
                  incrementUnreadMessages={incrementUnreadMessages}
                  messageFiles={messageFiles}
                  uploadFileViaButton={uploadFileViaButton}
                  documentPreviewIcon={documentPreviewIcon}
                  setMessageFiles={setMessageFiles}
                />
              </div>
            </div>
          </div>
        </Menu>
      </div>
    </div>
  );
};

export default function Chatbar(props) {
  const navigate = useNavigate();
  const isMobileView = useContext(IsMobileContext);
  if (!isMobileView) {
    if (!props.openedChannel && props.room !== "admin") {
      navigate("/");
    }
    return (
      <div>
        <div
          style={{
            width: "100%",
            height: "100%",
          }}
        >
          <ChatbarView
            userChannels={props.userChannels}
            openedChannel={props.openedChannel}
            handleChannelClick={props.handleChannelClick}
            chatPartnerStatus={props.chatPartnerStatus}
          />
        </div>
      </div>
    );
  }
}
